import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, ChevronLeft, Check, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Stars } from "../components/ProductCard";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-[#5A6B5D]">Product not found.</p>
        <Link to="/shop" className="text-[#1F4D2E] font-semibold underline mt-4 inline-block">
          Back to Shop
        </Link>
      </div>
    );

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#8A9A8D]">Loading...</div>;

  const discount =
    product.price_original > product.price_discounted
      ? Math.round(((product.price_original - product.price_discounted) / product.price_original) * 100)
      : 0;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  const buyNow = () => {
    addItem(product, qty);
    navigate("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[#5A6B5D] hover:text-[#1F4D2E] mb-6"
        data-testid="product-back-btn"
      >
        <ChevronLeft size={18} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="relative bg-white rounded-3xl border border-[#E2DAD0] overflow-hidden">
          {product.bestseller && (
            <span className="absolute top-4 left-4 z-10 bg-[#1F4D2E] text-[#F5F0E6] text-xs font-bold tracking-wider px-3 py-1.5 rounded-full">
              BESTSELLER
            </span>
          )}
          <img
            src={resolveImg(product.image_url)}
            alt={product.name}
            className="w-full aspect-square object-cover"
            data-testid="product-detail-image"
          />
        </div>

        <div>
          <h1 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20] leading-tight" data-testid="product-detail-name">
            {product.name}
          </h1>
          <div className="mt-4 flex items-center gap-2">
            <Stars rating={product.rating} />
            <span className="text-sm text-[#5A6B5D]">
              {product.rating} ({product.reviews_count} reviews)
            </span>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <span className="text-3xl font-bold text-[#1F4D2E]">{inr(product.price_discounted)}</span>
            {discount > 0 && (
              <>
                <span className="text-lg text-[#9E9E9E] line-through">{inr(product.price_original)}</span>
                <span className="bg-[#C5A059] text-white text-xs font-bold px-2.5 py-1 rounded-full">{discount}% OFF</span>
              </>
            )}
          </div>

          <div className="mt-3">
            {product.stock > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-[#1F4D2E] font-medium">
                <Check size={16} /> In Stock ({product.stock} available)
              </span>
            ) : (
              <span className="text-sm text-[#C86D51] font-medium">Out of Stock</span>
            )}
          </div>

          <p className="mt-5 text-[#5A6B5D] leading-relaxed" data-testid="product-detail-description">
            {product.description}
          </p>

          {/* Quantity */}
          <div className="mt-7 flex items-center gap-4">
            <span className="text-sm font-medium text-[#1C2B20]">Quantity</span>
            <div className="flex items-center border border-[#E2DAD0] rounded-full bg-white">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-2.5 text-[#1F4D2E] hover:bg-[#F5F0E6] rounded-l-full"
                data-testid="qty-decrease"
              >
                <Minus size={16} />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="p-2.5 text-[#1F4D2E] hover:bg-[#F5F0E6] rounded-r-full"
                data-testid="qty-increase"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAdd}
              disabled={product.stock <= 0}
              data-testid="product-add-to-cart-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] disabled:opacity-50 text-white font-medium rounded-full py-3.5 transition-colors"
            >
              <ShoppingCart size={18} /> Add to Cart
            </button>
            <button
              onClick={buyNow}
              disabled={product.stock <= 0}
              data-testid="product-buy-now-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#1F4D2E] text-[#1F4D2E] hover:bg-[#1F4D2E] hover:text-white disabled:opacity-50 font-medium rounded-full py-3.5 transition-colors"
            >
              Buy Now
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 border-t border-[#E2DAD0] pt-6">
            <div className="flex items-center gap-2 text-sm text-[#5A6B5D]">
              <Truck size={18} className="text-[#1F4D2E]" /> Cash on Delivery available
            </div>
            <div className="flex items-center gap-2 text-sm text-[#5A6B5D]">
              <ShieldCheck size={18} className="text-[#1F4D2E]" /> 100% Authentic & Natural
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
